import React, { useState, useRef, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Tabs, Steps } from 'antd';
import { UserOutlined, LockOutlined, MailOutlined, SafetyOutlined, CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import api from '../utils/api';

const { Title, Text } = Typography;

function ForgotPassword({ onBack }) {
  const [step, setStep] = useState(0); // 0=email, 1=otp, 2=new password, 3=done
  const [loading, setLoading] = useState(false);
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [countdown, setCountdown] = useState(0);
  const otpRefs = useRef([]);

  useEffect(() => {
    if (countdown <= 0) return;
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown]);

  const sendOtp = async () => {
    if (!emailOrUsername.trim()) { message.error('Enter your username or email'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { email_or_username: emailOrUsername.trim() });
      setMaskedEmail(res.data.masked_email || '');
      message.success(res.data.message || 'OTP sent!');
      setStep(1);
      setCountdown(60);
      setTimeout(() => otpRefs.current[0]?.focus(), 200);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to send OTP');
    }
    setLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const verifyOtp = async () => {
    const otpStr = otp.join('');
    if (otpStr.length !== 6) { message.error('Enter complete 6-digit OTP'); return; }
    setLoading(true);
    try {
      const res = await api.post('/auth/verify-otp', { email_or_username: emailOrUsername.trim(), otp: otpStr });
      setResetToken(res.data.reset_token);
      message.success('OTP verified!');
      setStep(2);
    } catch (err) {
      message.error(err.response?.data?.error || 'Invalid OTP');
    }
    setLoading(false);
  };

  const resetPassword = async (values) => {
    if (values.new_password !== values.confirm_password) { message.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.post('/auth/reset-password', {
        email_or_username: emailOrUsername.trim(),
        reset_token: resetToken,
        new_password: values.new_password,
      });
      message.success('Password reset successfully!');
      setStep(3);
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to reset password');
    }
    setLoading(false);
  };

  const resendOtp = async () => {
    if (countdown > 0) return;
    setOtp(['', '', '', '', '', '']);
    await sendOtp();
  };

  return (
    <div>
      <Button type="link" icon={<ArrowLeftOutlined />} onClick={onBack} style={{ padding: 0, marginBottom: 16, color: '#667eea' }}>
        Back to Login
      </Button>

      <Steps current={step} size="small" style={{ marginBottom: 24 }} items={[
        { title: 'Email' },
        { title: 'OTP' },
        { title: 'New Password' },
        { title: 'Done' },
      ]} />

      {step === 0 && (
        <div>
          <Text style={{ display: 'block', marginBottom: 16, color: '#666' }}>
            Enter your username or email address. We'll send a 6-digit OTP to your registered email.
          </Text>
          <Input
            size="large"
            prefix={<UserOutlined />}
            placeholder="Username or Email"
            value={emailOrUsername}
            onChange={e => setEmailOrUsername(e.target.value)}
            onPressEnter={sendOtp}
            style={{ marginBottom: 16 }}
          />
          <Button type="primary" loading={loading} block onClick={sendOtp}
            style={{ height: 44, borderRadius: 8, background: '#667eea' }}>
            Send OTP
          </Button>
        </div>
      )}

      {step === 1 && (
        <div>
          <Text style={{ display: 'block', marginBottom: 8, color: '#666' }}>
            OTP sent to <strong>{maskedEmail}</strong>
          </Text>
          <Text style={{ display: 'block', marginBottom: 16, color: '#999', fontSize: 12 }}>
            Enter the 6-digit OTP. Valid for 10 minutes.
          </Text>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginBottom: 16 }} onPaste={handleOtpPaste}>
            {otp.map((digit, i) => (
              <Input
                key={i}
                ref={el => otpRefs.current[i] = el}
                value={digit}
                onChange={e => handleOtpChange(i, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(i, e)}
                maxLength={1}
                style={{
                  width: 48, height: 52, textAlign: 'center', fontSize: 22, fontWeight: 'bold',
                  borderRadius: 8, border: '2px solid #d9d9d9', color: '#667eea'
                }}
              />
            ))}
          </div>
          <Button type="primary" loading={loading} block onClick={verifyOtp}
            style={{ height: 44, borderRadius: 8, background: '#667eea', marginBottom: 8 }}>
            <SafetyOutlined /> Verify OTP
          </Button>
          <div style={{ textAlign: 'center' }}>
            {countdown > 0 ? (
              <Text type="secondary" style={{ fontSize: 13 }}>Resend OTP in {countdown}s</Text>
            ) : (
              <Button type="link" onClick={resendOtp} style={{ color: '#667eea', fontSize: 13 }}>
                Resend OTP
              </Button>
            )}
          </div>
        </div>
      )}

      {step === 2 && (
        <Form onFinish={resetPassword} layout="vertical" size="large">
          <Text style={{ display: 'block', marginBottom: 16, color: '#666' }}>
            Set your new password (minimum 6 characters).
          </Text>
          <Form.Item name="new_password" rules={[{ required: true, min: 6, message: 'Min 6 characters' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="New Password" />
          </Form.Item>
          <Form.Item name="confirm_password" rules={[{ required: true, message: 'Confirm your password' }]}>
            <Input.Password prefix={<LockOutlined />} placeholder="Confirm New Password" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={loading} block
            style={{ height: 44, borderRadius: 8, background: '#667eea' }}>
            Reset Password
          </Button>
        </Form>
      )}

      {step === 3 && (
        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          <CheckCircleOutlined style={{ fontSize: 48, color: '#52c41a', marginBottom: 16 }} />
          <Title level={4} style={{ color: '#52c41a' }}>Password Reset Successful!</Title>
          <Text style={{ display: 'block', marginBottom: 20, color: '#666' }}>
            You can now login with your new password.
          </Text>
          <Button type="primary" block onClick={onBack}
            style={{ height: 44, borderRadius: 8, background: '#667eea' }}>
            Go to Login
          </Button>
        </div>
      )}
    </div>
  );
}

export default function Login({ onLogin }) {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('login');
  const [showForgot, setShowForgot] = useState(false);

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const res = await api.post('/auth/login', values);
      localStorage.setItem('token', res.data.token);
      localStorage.setItem('user', JSON.stringify(res.data.user));
      message.success('Login successful!');
      onLogin(res.data.user);
    } catch (err) {
      message.error(err.response?.data?.error || 'Login failed');
    }
    setLoading(false);
  };

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      await api.post('/auth/register', values);
      message.success('Registration successful! Waiting for admin approval.');
      setActiveTab('login');
    } catch (err) {
      message.error(err.response?.data?.error || 'Registration failed');
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <Card style={{ width: 420, borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src="/logo-full.png" alt="Bainsla Music" style={{ width: 180, height: 'auto', marginBottom: 12 }} />
          <Title level={3} style={{ margin: 0, color: '#1a1a2e' }}>Bainsla Music Data Dashboard</Title>
          <Text type="secondary">Bainsla Music • Intube Media • JPR Digital Media</Text>
        </div>

        {showForgot ? (
          <ForgotPassword onBack={() => setShowForgot(false)} />
        ) : (
          <Tabs activeKey={activeTab} onChange={setActiveTab} centered items={[
            {
              key: 'login',
              label: 'Login',
              children: (
                <Form onFinish={handleLogin} layout="vertical" size="large">
                  <Form.Item name="username" rules={[{ required: true, message: 'Username required' }]}>
                    <Input prefix={<UserOutlined />} placeholder="Username or Email" />
                  </Form.Item>
                  <Form.Item name="password" rules={[{ required: true, message: 'Password required' }]}>
                    <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block style={{ height: 44, borderRadius: 8, background: '#667eea' }}>
                      Login
                    </Button>
                  </Form.Item>
                  <div style={{ textAlign: 'center' }}>
                    <Button type="link" onClick={() => setShowForgot(true)} style={{ color: '#667eea', fontSize: 13, padding: 0 }}>
                      Forgot Password?
                    </Button>
                  </div>
                </Form>
              )
            },
            {
              key: 'register',
              label: 'Register',
              children: (
                <Form onFinish={handleRegister} layout="vertical" size="large">
                  <Form.Item name="full_name" rules={[{ required: true, message: 'Name required' }]}>
                    <Input prefix={<UserOutlined />} placeholder="Full Name" />
                  </Form.Item>
                  <Form.Item name="username" rules={[{ required: true, message: 'Username required' }]}>
                    <Input prefix={<UserOutlined />} placeholder="Username" />
                  </Form.Item>
                  <Form.Item name="email" rules={[{ required: true, type: 'email', message: 'Valid email required' }]}>
                    <Input prefix={<MailOutlined />} placeholder="Email" />
                  </Form.Item>
                  <Form.Item name="password" rules={[{ required: true, min: 6, message: 'Min 6 characters' }]}>
                    <Input.Password prefix={<LockOutlined />} placeholder="Password" />
                  </Form.Item>
                  <Form.Item>
                    <Button type="primary" htmlType="submit" loading={loading} block style={{ height: 44, borderRadius: 8, background: '#667eea' }}>
                      Register
                    </Button>
                  </Form.Item>
                </Form>
              )
            }
          ]} />
        )}
      </Card>
    </div>
  );
}
